import { FORM_TYPE_LIMIT, FormType } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useQuery } from '@tanstack/react-query';

import { AWAInspectionTemplatesList } from '@/awa-pages/[workspaceId]/assets/[id]/forms/list';
import { SkeletonList } from '@/awa-pages/[workspaceId]/assets/[id]/forms/skeleton-list';
import WarningIcon from '@/components/icons/warning';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getAssetFormsByTypeQueryOptions } from '@/queries/forms';
import { AWAApiV2Service } from '@/services/awa-api-v2';

export function AWAAssetInspectionTemplates({ assetId }: { assetId: string }) {
  const {
    data: inspectionTemplatesData,
    isLoading: inspectionTemplatesLoading,
    isError: inspectionTemplateFetchError,
  } = useQuery(getAssetFormsByTypeQueryOptions({ assetId, formType: FormType.InspectionForm, limit: FORM_TYPE_LIMIT[FormType.InspectionForm], isAWA: true }));

  if (inspectionTemplatesLoading) {
    return <SkeletonList />;
  }

  return (
    <div className="flex flex-col">
      {!inspectionTemplatesLoading && inspectionTemplatesData && (
        <AWAInspectionTemplatesList inspectionTemplates={inspectionTemplatesData || []} assetId={assetId} />
      )}

      <Dialog open={!inspectionTemplatesLoading && inspectionTemplateFetchError && !inspectionTemplatesData}>
        <DialogContent className="w-[354px] rounded-md p-0 sm:w-[500px]">
          <VisuallyHidden>
            <DialogTitle>Error</DialogTitle>
          </VisuallyHidden>
          <DialogHeader>
            <DialogDescription className="flex flex-col items-center gap-2 pt-6">
              <WarningIcon size={30} className="text-default-red" />
              <div className="text-default-text text-xl font-medium">Unable to load</div>
              <div className="text-default-text text-sm font-normal">An error has occurred with loading this asset's inspection forms.</div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mb-6 flex items-center sm:flex-col sm:space-x-0">
            <Button
              className="bg-default-blue mt-4 w-fit rounded-md p-2.5 text-sm font-medium"
              onClick={() => {
                location.reload();
              }}>
              Retry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
